import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  LinearProgress,
  Tooltip
} from '@mui/material';
import GradeIcon from '@mui/icons-material/Grade';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';
import useIsMobile from '../hooks/useIsMobile';
import InteractiveResponseViewer from './InteractiveResponseViewer';

interface StudentSubmissionSummary {
  student_id: number;
  student_name: string;
  grade_level: string | null;
  reading_level: number | null;
  has_tailored_document: boolean;
  submission_id: number | null;
  submission_status: string;
  submitted_at: string | null;
  grade: string | null;
  points_earned: number | null;
  graded_at: string | null;
  has_interactive_response?: boolean;
  interactive_response_id?: number;
  interactive_response_status?: string;
}

interface SubmissionDetail {
  id: number;
  assignment_id: number;
  student_id: number;
  student_name: string;
  status: string;
  submission_text: string | null;
  submission_notes: string | null;
  original_filename: string | null;
  has_file: boolean;
  grade: string | null;
  points_earned: number | null;
  points_possible: number | null;
  teacher_feedback: string | null;
  submitted_at: string | null;
  graded_at: string | null;
}

interface SubmissionsGradingProps {
  assignmentId: number;
  documentName: string;
  onClose: () => void;
}

const SubmissionsGrading: React.FC<SubmissionsGradingProps> = ({
  assignmentId,
  documentName,
  onClose
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { isMobile } = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [students, setStudents] = useState<StudentSubmissionSummary[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  
  // Grading dialog state
  const [gradingDialogOpen, setGradingDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionDetail | null>(null);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [grade, setGrade] = useState('');
  const [pointsEarned, setPointsEarned] = useState('');
  const [pointsPossible, setPointsPossible] = useState('100');
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Interactive response viewer state
  const [interactiveViewerOpen, setInteractiveViewerOpen] = useState(false);
  const [selectedInteractiveResponseId, setSelectedInteractiveResponseId] = useState<number | null>(null);
  const [selectedInteractiveStudentName, setSelectedInteractiveStudentName] = useState('');

  useEffect(() => {
    fetchSubmissionsSummary();
  }, [assignmentId]);

  const fetchSubmissionsSummary = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/assignments/${assignmentId}/submissions/summary`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        setStudents(response.data.students);
        setStats(response.data.stats);
        setDueDate(response.data.due_date);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenGrading = async (studentSummary: StudentSubmissionSummary) => {
    if (!studentSummary.submission_id) {
      return; // No submission to grade
    }
    
    setGradingDialogOpen(true);
    setLoadingSubmission(true);
    setSubmitError('');
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/submissions/${studentSummary.submission_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        const submission = response.data.submission;
        setSelectedSubmission(submission);
        setGrade(submission.grade || '');
        setPointsEarned(submission.points_earned?.toString() || '');
        setPointsPossible(submission.points_possible?.toString() || '100');
        setFeedback(submission.teacher_feedback || '');
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to load submission');
    } finally {
      setLoadingSubmission(false);
    }
  };

  const handleCloseGrading = () => {
    setGradingDialogOpen(false);
    setSelectedSubmission(null);
    setGrade('');
    setPointsEarned('');
    setFeedback('');
    setSubmitError('');
  };

  const handleGradeSubmission = async () => {
    if (!selectedSubmission) return;
    
    setSubmitting(true);
    setSubmitError('');
    
    try {
      await axios.post(
        `${API_BASE_URL}/api/submissions/${selectedSubmission.id}/grade`,
        {
          grade: grade || null,
          points_earned: pointsEarned ? parseFloat(pointsEarned) : null,
          points_possible: pointsPossible ? parseFloat(pointsPossible) : null,
          feedback: feedback || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Refresh the list
      await fetchSubmissionsSummary();
      handleCloseGrading();
      
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to grade submission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnForRevision = async () => {
    if (!selectedSubmission) return;
    
    setSubmitting(true);
    setSubmitError('');
    
    try {
      await axios.post(
        `${API_BASE_URL}/api/submissions/${selectedSubmission.id}/return`,
        {
          feedback: feedback || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Refresh the list
      await fetchSubmissionsSummary();
      handleCloseGrading();
      
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to return submission');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'graded':
        return <Chip label={t('grading.statuses.graded')} color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'submitted':
        return <Chip label={t('grading.statuses.submitted')} color="primary" size="small" icon={<PendingIcon />} />;
      case 'returned':
        return <Chip label={t('grading.statuses.returned')} color="warning" size="small" icon={<ReplayIcon />} />;
      case 'draft':
        return <Chip label={t('grading.statuses.draft')} color="default" size="small" icon={<EditIcon />} />;
      default:
        return <Chip label={t('grading.statuses.notStarted')} color="default" size="small" variant="outlined" />;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('grading.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        p: 2,
        bgcolor: '#f5f5f5',
        borderRadius: 1
      }}>
        <Box>
          <Typography variant="h5" component="h2">
            {documentName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('grading.subtitle')}
            {dueDate && ` • ${t('grading.dueDate')}: ${formatDate(dueDate)}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Stats */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
            <Typography variant="h4">{stats.total}</Typography>
            <Typography variant="caption">{t('grading.stats.total')}</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="h4">{stats.submitted}</Typography>
            <Typography variant="caption">{t('grading.stats.submitted')}</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center', bgcolor: '#e8f5e9' }}>
            <Typography variant="h4">{stats.graded}</Typography>
            <Typography variant="caption">{t('grading.stats.graded')}</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center', bgcolor: '#fff3e0' }}>
            <Typography variant="h4">{stats.not_started}</Typography>
            <Typography variant="caption">{t('grading.stats.notStarted')}</Typography>
          </Paper>
        </Box>
      )}

      {/* Progress */}
      {stats && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">{t('grading.gradingProgress')}</Typography>
            <Typography variant="body2">
              {stats.graded} / {stats.total} ({Math.round((stats.graded / stats.total) * 100)}%)
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(stats.graded / stats.total) * 100} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {/* Students List */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {students.map((student) => (
            <Paper
              key={student.student_id}
              sx={{ p: 2, borderRadius: 2, opacity: student.submission_status === 'not_started' ? 0.7 : 1 }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box minWidth={0}>
                  <Typography variant="subtitle2">{student.student_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {student.grade_level && `${student.grade_level}`}
                    {student.reading_level && ` • RL: ${student.reading_level}`}
                  </Typography>
                </Box>
                {getStatusChip(student.submission_status)}
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1}>
                {student.has_tailored_document && <Chip label={t('grading.tailored')} size="small" sx={{ height: 20 }} />}
                {student.has_interactive_response && (
                  <Chip label={t('grading.interactive')} size="small"
                    color={student.interactive_response_status === 'submitted' ? 'success' : student.interactive_response_status === 'returned' ? 'info' : 'warning'}
                    sx={{ height: 20 }}
                  />
                )}
                {student.grade ? (
                  <Chip label={student.grade} size="small" variant="outlined" />
                ) : student.points_earned !== null ? (
                  <Chip label={`${student.points_earned}`} size="small" variant="outlined" />
                ) : null}
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {student.has_interactive_response && student.interactive_response_id && (
                  <Button variant="contained" size="small" startIcon={<TouchAppIcon />}
                    onClick={() => { setSelectedInteractiveResponseId(student.interactive_response_id!); setSelectedInteractiveStudentName(student.student_name); setInteractiveViewerOpen(true); }}
                    sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, flex: 1 }}
                    aria-label={`${t('grading.viewInteractive')} - ${student.student_name}`}
                  >
                    {t('grading.viewInteractive')}
                  </Button>
                )}
                {student.submission_id ? (
                  <Button variant={student.submission_status === 'submitted' ? 'contained' : 'outlined'} size="small"
                    startIcon={student.submission_status === 'graded' ? <VisibilityIcon /> : <GradeIcon />}
                    onClick={() => handleOpenGrading(student)} sx={{ flex: 1 }}
                    aria-label={`${student.submission_status === 'graded' ? t('grading.view') : t('grading.grade')} - ${student.student_name}`}
                  >
                    {student.submission_status === 'graded' ? t('grading.view') : t('grading.grade')}
                  </Button>
                ) : !student.has_interactive_response ? (
                  <Typography variant="caption" color="text.secondary">{t('grading.noSubmission')}</Typography>
                ) : null}
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label={t('grading.tableLabel')}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>{t('grading.columns.student')}</TableCell>
                <TableCell>{t('grading.columns.status')}</TableCell>
                <TableCell>{t('grading.columns.submittedAt')}</TableCell>
                <TableCell>{t('grading.columns.grade')}</TableCell>
                <TableCell align="right">{t('grading.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((student) => (
                <TableRow key={student.student_id}
                  sx={{ '&:hover': { bgcolor: '#fafafa' }, opacity: student.submission_status === 'not_started' ? 0.7 : 1 }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body1">{student.student_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {student.grade_level && `${student.grade_level}`}
                        {student.reading_level && ` • RL: ${student.reading_level}`}
                        {student.has_tailored_document && <Chip label={t('grading.tailored')} size="small" sx={{ ml: 1, height: 18 }} />}
                        {student.has_interactive_response && (
                          <Chip label={t('grading.interactive')} size="small"
                            color={student.interactive_response_status === 'submitted' ? 'success' : student.interactive_response_status === 'returned' ? 'info' : 'warning'}
                            sx={{ ml: 0.5, height: 18 }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(student.submission_status)}</TableCell>
                  <TableCell>{formatDate(student.submitted_at)}</TableCell>
                  <TableCell>{student.grade || (student.points_earned !== null ? `${student.points_earned}` : '-')}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {student.has_interactive_response && student.interactive_response_id && (
                        <Tooltip title={t('grading.viewInteractive')}>
                          <Button variant="contained" size="small" startIcon={<TouchAppIcon />}
                            onClick={() => { setSelectedInteractiveResponseId(student.interactive_response_id!); setSelectedInteractiveStudentName(student.student_name); setInteractiveViewerOpen(true); }}
                            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                          >{t('grading.viewInteractive')}</Button>
                        </Tooltip>
                      )}
                      {student.submission_id ? (
                        <Tooltip title={student.submission_status === 'graded' ? t('grading.viewGrade') : t('grading.gradeSubmission')}>
                          <Button variant={student.submission_status === 'submitted' ? 'contained' : 'outlined'} size="small"
                            startIcon={student.submission_status === 'graded' ? <VisibilityIcon /> : <GradeIcon />}
                            onClick={() => handleOpenGrading(student)}
                          >{student.submission_status === 'graded' ? t('grading.view') : t('grading.grade')}</Button>
                        </Tooltip>
                      ) : !student.has_interactive_response ? (
                        <Typography variant="caption" color="text.secondary">{t('grading.noSubmission')}</Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Grading Dialog */}
      <Dialog
        open={gradingDialogOpen}
        onClose={handleCloseGrading}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {t('grading.dialog.title')} - {selectedSubmission?.student_name}
        </DialogTitle>
        <DialogContent>
          {loadingSubmission ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedSubmission ? (
            <Box>
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>
              )}
              
              {/* Student's Work */}
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                {t('grading.dialog.studentWork')}:
              </Typography>
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#fafafa', maxHeight: 200, overflow: 'auto' }}>
                {selectedSubmission.submission_text ? (
                  <Typography style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedSubmission.submission_text}
                  </Typography>
                ) : (
                  <Typography color="text.secondary" fontStyle="italic">
                    {t('grading.dialog.noTextSubmission')}
                  </Typography>
                )}
              </Paper>

              {selectedSubmission.submission_notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('grading.dialog.studentNotes')}:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#fff8e1' }}>
                    <Typography>{selectedSubmission.submission_notes}</Typography>
                  </Paper>
                </Box>
              )}

              {selectedSubmission.has_file && selectedSubmission.original_filename && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('grading.dialog.uploadedFile')}:
                  </Typography>
                  <Chip 
                    label={selectedSubmission.original_filename}
                    variant="outlined"
                  />
                </Box>
              )}

              {/* Grading Fields */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label={t('grading.dialog.letterGrade')}
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  placeholder="A, B+, etc."
                  sx={{ width: 120 }}
                  disabled={submitting}
                  aria-label={t('grading.dialog.letterGrade')}
                />
                <TextField
                  label={t('grading.dialog.pointsEarned')}
                  type="number"
                  value={pointsEarned}
                  onChange={(e) => setPointsEarned(e.target.value)}
                  sx={{ width: 120 }}
                  disabled={submitting}
                  aria-label={t('grading.dialog.pointsEarned')}
                />
                <TextField
                  label={t('grading.dialog.pointsPossible')}
                  type="number"
                  value={pointsPossible}
                  onChange={(e) => setPointsPossible(e.target.value)}
                  sx={{ width: 120 }}
                  disabled={submitting}
                  aria-label={t('grading.dialog.pointsPossible')}
                />
              </Box>

              <TextField
                label={t('grading.dialog.feedback')}
                multiline
                rows={4}
                fullWidth
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={t('grading.dialog.feedbackPlaceholder')}
                disabled={submitting}
                aria-label={t('grading.dialog.feedback')}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGrading} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          {selectedSubmission?.status !== 'graded' && (
            <Button 
              onClick={handleReturnForRevision}
              disabled={submitting}
              color="warning"
              startIcon={<ReplayIcon />}
            >
              {submitting ? <CircularProgress size={20} /> : t('grading.dialog.returnForRevision')}
            </Button>
          )}
          <Button 
            variant="contained"
            onClick={handleGradeSubmission}
            disabled={submitting}
            startIcon={<GradeIcon />}
          >
            {submitting ? <CircularProgress size={20} /> : t('grading.dialog.saveGrade')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interactive Response Viewer */}
      {selectedInteractiveResponseId && (
        <InteractiveResponseViewer
          open={interactiveViewerOpen}
          responseId={selectedInteractiveResponseId}
          studentName={selectedInteractiveStudentName}
          onClose={() => {
            setInteractiveViewerOpen(false);
            setSelectedInteractiveResponseId(null);
          }}
          onGraded={() => {
            fetchSubmissionsSummary();
          }}
        />
      )}
    </Box>
  );
};

export default SubmissionsGrading;

